import type { FC, MutableRefObject } from 'react';

import type { SectorPathData } from '../utils/svgParser';
import type { Point2D, SceneRuntime, SectorPreviewTransform } from '../models/types';

const SECTOR_HIT_STROKE_WIDTH = 12;

const hasClosedSectorArea = (path: string, type: 'path' | 'rect') => {
  if (type === 'rect') return true;
  return /(?:z)\s*$/i.test(path.trim());
};

type SectorOverlayProps = {
  cameraSnapshot: {
    x: number;
    y: number;
    scale: number;
  };
  canvasWidth: number;
  canvasHeight: number;
  scene: SceneRuntime | null;
  normalizedOffset: Point2D | null;
  sectorPreviewTransform: SectorPreviewTransform;
  sectorPaths: SectorPathData[];
  sectorHitPathRefs: MutableRefObject<Map<string, SVGPathElement>>;
};

const SectorOverlay: FC<SectorOverlayProps> = ({
  cameraSnapshot,
  canvasWidth,
  canvasHeight,
  scene,
  normalizedOffset,
  sectorPreviewTransform,
  sectorPaths,
  sectorHitPathRefs,
}) => {
  if (!scene || !normalizedOffset || sectorPaths.length === 0) return null;

  return (
    <svg
      className="absolute inset-0 pointer-events-none z-[5]"
      width={canvasWidth}
      height={canvasHeight}
      viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
      aria-hidden="true"
    >
      <g transform={`translate(${cameraSnapshot.x} ${cameraSnapshot.y}) scale(${cameraSnapshot.scale})`}>
        <g
          transform={`translate(${scene.contentRoot.position.x} ${scene.contentRoot.position.y}) rotate(${(scene.contentRoot.rotation * 180) / Math.PI}) translate(${-scene.contentRoot.pivot.x} ${-scene.contentRoot.pivot.y})`}
        >
          <g
            transform={[
              `translate(${normalizedOffset.x + sectorPreviewTransform.x} ${normalizedOffset.y + sectorPreviewTransform.y})`,
              `scale(${sectorPreviewTransform.scaleX} ${sectorPreviewTransform.scaleY})`,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {sectorPaths.map(sector => (
              <path
                key={`sector-hit-${sector.name}`}
                ref={node => {
                  if (node) {
                    sectorHitPathRefs.current.set(sector.name, node);
                  } else {
                    sectorHitPathRefs.current.delete(sector.name);
                  }
                }}
                d={sector.path}
                fill="rgba(0,0,0,0.01)"
                stroke="transparent"
                strokeWidth={hasClosedSectorArea(sector.path, sector.type) ? 0 : SECTOR_HIT_STROKE_WIDTH}
                opacity={0}
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </g>
        </g>
      </g>
    </svg>
  );
};

export default SectorOverlay;
